import React from 'react';
import {ButtonBase, Grid, Typography} from '@mui/material';
import {SeverityColor} from '../../constants/securspace-constants';
import {styled, Theme} from "@mui/material/styles";

// maybe i should fit this into the global styles somewhere :thinking:
const backgroundColorMap = {
  light: '#FFF',
  dark: '#2b2b2bff'
}

interface InfoBoxSeverityProps {
  severity: SeverityColor;
  listHover?: boolean;
  pinColor?: string;
}

const resolveColor = (theme: Theme, severity: SeverityColor, pinColor?: string) => pinColor ?? theme.palette[severity].main;

const makeBorder = (severity: SeverityColor, theme: Theme, pinColor?: string) => `0.15em solid ${resolveColor(theme, severity, pinColor)}`;

export const InfoBoxInnerContent = styled(Grid, {
  shouldForwardProp: prop => prop !== 'severity' && prop !== 'listHover' && prop !== 'pinColor'
})<InfoBoxSeverityProps>(({theme, severity, listHover, pinColor}) => ({
  background: listHover ? backgroundColorMap.dark : backgroundColorMap.light,
  border: makeBorder(severity, theme, pinColor),
  left: '0',
  top: '0',
  borderRadius: '.727272727272em',
  padding: '.4em 0.65em',
}));

export const InfoBoxPointyThing = styled('div', {
  shouldForwardProp: prop => prop !== 'severity' && prop !== 'listHover' && prop !== 'pinColor'
})<InfoBoxSeverityProps>(({theme, severity, listHover, pinColor}) => ({
  background: listHover ? backgroundColorMap.dark : backgroundColorMap.light,
  borderBottom: makeBorder(severity, theme, pinColor),
  borderRight: makeBorder(severity, theme, pinColor),
  borderRadius: '90% 0 0 0',
  width: '0.75em',
  height: '0.75em',
  transform: 'rotate(45deg)',
  marginTop: '-.45em',
  marginLeft: '42.75%',
}));

export const InfoBoxTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'listHover'
})<{listHover?: boolean}>(({listHover}) => ({
  color: listHover ? backgroundColorMap.light : backgroundColorMap.dark,
  fontFamily: 'Inter',
  fontSize: '1.2rem',
  fontWeight: '500',
  lineHeight: '0.9rem',
  letterSpacing: '0',
  textAlign: 'left',
}));

export const InfoBoxCircle = styled('div', {
  shouldForwardProp: prop => prop !== 'severity' && prop !== 'pinColor'
})<{severity: SeverityColor, pinColor?: string}>(({theme, severity, pinColor}) => ({
  backgroundColor: resolveColor(theme, severity, pinColor),
  clipPath: 'circle(50%)',
  width: '0.909090909em',
  height: '0.909090909em',
}));

export const InfoBoxSpacer = styled('div')(() => ({
  width: '0.818181818181em',
  height: '0.818181818181em',
}));

interface LocationPriceInfoBoxBaseProps {
  formattedPrice: string;
  severity?: SeverityColor;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  listHover?: boolean;
  /** Raw color override (e.g. a gray for "at capacity" pins) that takes priority over the severity-derived theme color. */
  pinColor?: string;
}

const LocationPriceInfoBoxBase = (
  props: LocationPriceInfoBoxBaseProps) => {
  const { formattedPrice, onClick, severity = 'success', listHover, pinColor } = props;

  return <>
    <ButtonBase onClick={onClick}>
      <InfoBoxInnerContent container
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-around'}
        wrap={'nowrap'}
        listHover={listHover}
        severity={severity}
        pinColor={pinColor}
      >
        <Grid item>
          <InfoBoxCircle severity={severity} pinColor={pinColor} />
        </Grid>
        <Grid item>
          <InfoBoxSpacer />
        </Grid>
        <Grid item>
          <InfoBoxTypography listHover={listHover}>{formattedPrice}</InfoBoxTypography>
        </Grid>
      </InfoBoxInnerContent>
    </ButtonBase>
    <InfoBoxPointyThing listHover={listHover} severity={severity} pinColor={pinColor} />
  </>
}

export default LocationPriceInfoBoxBase;
